// CSV import / export dialog. Used by the contact book.

import { useEffect, useState } from "react";

import { ApiError, http } from "../api";
import {
  Banner,
  Dialog,
  DialogFooter,
  Field,
  SkeletonRows,
  Spinner,
  Tabs,
  useToast,
} from "../ui";
import { errMessage, downloadBlob } from "./shared";

// ------------------------------------------------------------ import / export

/** Local YYYY-MM-DD. Never UTC: an export named "today" means the operator's today. */
function localDate(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

interface ColumnOption {
  key: string;
  label: string;
}

interface ExportConfig {
  table: string;
  columns: ColumnOption[];
  default_columns: string[];
}

interface ImportConfig {
  table: string;
  required_columns: ColumnOption[];
  optional_columns: ColumnOption[];
  unique_column: string;
}

interface ImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  messages: string[];
}

export function ImportExportDialog({
  open,
  onClose,
  table,
  tableLabel,
  filters,
  canImport,
  canExport,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  table: string;
  tableLabel: string;
  filters?: Record<string, string>;
  canImport: boolean;
  canExport: boolean;
  onImported: () => void;
}) {
  const toast = useToast();
  const [tab, setTab] = useState(canExport ? "export" : "import");

  const [exportConfig, setExportConfig] = useState<ExportConfig | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);

  const [importConfig, setImportConfig] = useState<ImportConfig | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [updateOnDuplicate, setUpdateOnDuplicate] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [configError, setConfigError] = useState<ApiError | null>(null);

  // A role with only one of the two permissions must land on the tab it can use.
  useEffect(() => {
    if (!canExport && tab === "export") setTab("import");
    if (!canImport && tab === "import") setTab("export");
  }, [canExport, canImport, tab]);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setConfigError(null);
    if (canExport) {
      void http
        .get<ExportConfig>(`/export/${table}/config`)
        .then((cfg) => {
          setExportConfig(cfg);
          setSelected(cfg.default_columns);
        })
        .catch((err: unknown) =>
          setConfigError(err instanceof ApiError ? err : new ApiError(0, "client_error", String(err), ""))
        );
    }
    if (canImport) {
      void http
        .get<ImportConfig>(`/import/${table}/config`)
        .then(setImportConfig)
        .catch((err: unknown) =>
          setConfigError(err instanceof ApiError ? err : new ApiError(0, "client_error", String(err), ""))
        );
    }
  }, [open, table, canExport, canImport]);

  const runExport = async () => {
    setExporting(true);
    try {
      const res = await http.raw("/export", {
        method: "POST",
        body: { table, columns: selected, filters: filters ?? {}, format: "csv" },
      });
      downloadBlob(await res.blob(), `${table}_export_${localDate(new Date())}.csv`);
      toast.push(`${tableLabel} exported`, "good");
    } catch (err) {
      toast.push(errMessage(err, "Could not export"), "critical");
    } finally {
      setExporting(false);
    }
  };

  const downloadSample = () => {
    if (!importConfig) return;
    const headers = [
      ...importConfig.required_columns.map((c) => c.key),
      ...importConfig.optional_columns.map((c) => c.key),
    ];
    downloadBlob(new Blob([`${headers.join(",")}\n`], { type: "text/csv" }), `${table}_sample.csv`);
  };

  const runImport = async () => {
    if (!file) {
      toast.push("Choose a CSV file first", "critical");
      return;
    }
    setImporting(true);
    try {
      const form = new FormData();
      form.append("table", table);
      form.append("file", file);
      if (updateOnDuplicate) form.append("update_on_duplicate", "true");
      const res = await http.upload<ImportResult>("/import", form);
      setResult(res);
      toast.push(`Imported: ${res.created} created, ${res.updated} updated`, res.errors ? "warning" : "good");
      // The dialog stays open on purpose: the counters below are the report.
      onImported();
    } catch (err) {
      toast.push(errMessage(err, "Could not import the file"), "critical");
    } finally {
      setImporting(false);
    }
  };

  const tabs = [
    ...(canExport ? [{ id: "export", label: "Export" }] : []),
    ...(canImport ? [{ id: "import", label: "Import" }] : []),
  ];

  return (
    <Dialog open={open} onClose={onClose} title={`Import / export ${tableLabel.toLowerCase()}`} wide>
      {configError && (
        <Banner tone="critical" traceId={configError.traceId}>
          Could not load the import/export options: {configError.message}
        </Banner>
      )}

      {tabs.length > 1 && <Tabs tabs={tabs} active={tab} onChange={setTab} />}

      {tab === "export" && canExport && (
        <>
          {filters?.search && (
            <Banner tone="info">
              Only the rows matching &quot;{filters.search}&quot; are exported.
            </Banner>
          )}
          <div className="section">
            <h4>Columns</h4>
            {exportConfig ? (
              <div className="chips">
                {exportConfig.columns.map((c) => (
                  <label className="check" key={c.key}>
                    <input
                      type="checkbox"
                      checked={selected.includes(c.key)}
                      onChange={() =>
                        setSelected((prev) =>
                          prev.includes(c.key) ? prev.filter((k) => k !== c.key) : [...prev, c.key]
                        )
                      }
                    />
                    {c.label}
                  </label>
                ))}
              </div>
            ) : (
              <SkeletonRows n={2} cols={3} />
            )}
          </div>
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <button
              className="btn primary"
              onClick={() => void runExport()}
              disabled={exporting || selected.length === 0}
            >
              {exporting && <Spinner label="Exporting" />}
              Export CSV
            </button>
          </div>
        </>
      )}

      {tab === "import" && canImport && (
        <>
          {importConfig ? (
            <>
              <div className="section">
                <h4>Required columns</h4>
                <div className="chips">
                  {importConfig.required_columns.map((c) => (
                    <code className="mono" key={c.key}>
                      {c.key}
                    </code>
                  ))}
                </div>
              </div>
              {importConfig.optional_columns.length > 0 && (
                <div className="section">
                  <h4>Optional columns</h4>
                  <div className="chips">
                    {importConfig.optional_columns.map((c) => (
                      <code className="mono" key={c.key}>
                        {c.key}
                      </code>
                    ))}
                  </div>
                </div>
              )}
              <button className="btn sm" onClick={downloadSample}>
                ⭳ Download a sample CSV
              </button>
            </>
          ) : (
            <SkeletonRows n={2} cols={3} />
          )}

          <Field label="CSV file" required>
            <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </Field>

          {importConfig?.unique_column && (
            <label className="check">
              <input
                type="checkbox"
                checked={updateOnDuplicate}
                onChange={(e) => setUpdateOnDuplicate(e.target.checked)}
              />
              Update the existing row when {importConfig.unique_column} already exists
            </label>
          )}

          {result && (
            <Banner tone={result.errors ? "warning" : "good"}>
              {result.created} created · {result.updated} updated · {result.skipped} skipped ·{" "}
              {result.errors} errors
              {result.messages.length > 0 && (
                <div className="pre-block">{result.messages.join("\n")}</div>
              )}
            </Banner>
          )}

          <div className="row" style={{ justifyContent: "flex-end" }}>
            <button className="btn primary" onClick={() => void runImport()} disabled={!file || importing}>
              {importing && <Spinner label="Importing" />}
              Import
            </button>
          </div>
        </>
      )}

      <DialogFooter>
        <button className="btn" onClick={onClose}>
          Close
        </button>
      </DialogFooter>
    </Dialog>
  );
}
